/* eslint-disable jsx-a11y/anchor-is-valid */
"use client";

import styled from "@emotion/styled";
import { useContext, useEffect, useState } from "react";
import { Button } from "@mui/material";
import LogoDsKo from "../assets/icons/LogoDsKo";
import IcoExternal from "../assets/icons/IcoExternal";
import Badge from "./badge";
import { useScrollDetection } from "../hooks/useScrollDetection";
import { MyContext } from "../context/MyContext";
import { backIncstance } from "../utils/commonFunctions";
import { useNavigate } from "react-router-dom";
import { useUserQuery } from "../views/tutorV2/hooks/queries/useUserQuery";
import { useAuth } from "../views/tutorV2/hooks/useAuth";

const ACCOUNT_DOMAIN =
  process.env.REACT_APP_LOGIN_URL || "https://account.dongascience.com";

const URLS = {
  LOGOUT: process.env.REACT_APP_LOGOUT_URL,
};

// Styled component for the header
const StyledCommonHeaderTop = styled.header<{
  isSticky?: boolean;
  isMobile?: boolean;
  top?: number;
}>`
  background-color: white;
  padding: 0 24px;
  box-sizing: border-box;
  width: 100%;
  position: fixed;
  top: ${(props) => props.top ?? 0}px;
  left: 0;
  right: 0;
  z-index: ${(props) => (props.isSticky ? 1200 : 999)};
  box-shadow: ${(props) =>
    props.isSticky && !props.isMobile ? "0 2px 8px rgba(0,0,0,0.08)" : "none"};
  transition: box-shadow 0.2s ease, background-color 0.2s ease;

  .wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    max-width: 1240px;
    width: 100%;
    height: ${(props) => (props.isSticky ? "56px" : "140px")};
    margin: 0 auto;
    transition: height 240ms ease, padding 240ms ease;
    overflow: hidden;
  }
  .left {
    display: flex;
    align-items: center;
    gap: 36px;
    flex: 1;
    .logo {
      max-width: ${(props) => (props.isSticky ? "128px" : "158px")};
      transition: max-width 240ms ease, transform 240ms ease;
      cursor: pointer;
    }
    nav {
      display: flex;
      align-items: center;
      gap: 24px;
      width: 100%;
      .mobile-icons {
        display: flex;
        gap: 12px;
        margin-left: auto;
        margin-right: 28px;
        align-items: center;
      }
      .mobile-icon-link img {
        width: 24px;
        height: 24px;
        object-fit: contain;
        display: block;
      }
      .external-icon {
        width: 12px;
        height: 12px;
        color: #757575;
        margin-bottom: 1px;
        margin-left: 4px;
      }
      a span {
        position: relative;
        font-family: "Noto Sans KR";
        font-size: ${(props) => (props.isSticky ? "14px" : "18px")};
        transition: font-size 240ms ease;
        font-style: normal;
        font-weight: 500;
        line-height: 24px;
        color: #424242;
        &.external-span {
          color: #757575;
          font-size: 16px;
        }
        &.active {
          font-size: ${(props) => (props.isSticky ? "16px" : "20px")};
          font-style: normal;
          font-weight: 700;
          line-height: 24px; /* 120% */
          letter-spacing: -0.6px;
          color: #212121;
        }
        .svg-area {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          top: -4px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }
      a:hover {
        span,
        .external-icon {
          color: #212121;
        }
      }
    }
  }
  .right-wrapper {
    display: flex;
    gap: 20px;
    align-items: center;
    .login-link,
    .logout-btn,
    .member-wrap {
      color: #212121;
      font-family: Pretendard;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 40px; /* 285.714% */
    }
    .logout-btn {
      color: #666666;
    }
    .member-wrap-flex {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }
  .icons {
    display: flex;
    gap: 16px;
    align-items: center;
    i {
      width: 24px;
      height: 24px;
    }

    .link-icon-text {
      display: flex;
      align-items: center;
      gap: 2px;
      .link-text {
        color: #212121;
        font-size: 14px;
        font-weight: 400;
        letter-spacing: -0.42px;
      }
    }

    ${(props) =>
      props.isSticky || props.isMobile
        ? `
      & > *:not(.search-btn) { display: none; }
    `
        : ""}
  }
  .mobile-wrapper {
    display: none;
  }
  @media (max-width: 768px) {
    padding: 0 16px;
    .wrapper {
      height: ${(props) => (props.isSticky ? "56px" : "96px")};
      align-items: ${(props) => (props.isSticky ? "center" : "flex-start")};
      margin-top: ${(props) => (props.isSticky ? "0" : "19px")};
      .left {
        flex-direction: ${(props) => (props.isSticky ? "row" : "column")};
        align-items: ${(props) => (props.isSticky ? "center" : "flex-start")};
        gap: 22px;
        nav {
          gap: 16px;
        }
      }
    }
    .logo {
      width: 128px;
    }
    span {
      font-size: 14px !important;
    }
    .mobile-icons {
      ${(props) => props.isSticky && "display: none !important;"}
    }
    .account-area {
      display: none;
    }
    .mobile-wrapper {
      display: flex;
      flex-direction: column;
      height: 96px;
    }
    .mobile-top-row {
      display: flex;
      align-items: center;
      height: 56px;
    }
    .mobile-logo {
      width: 128px;
      cursor: pointer;
    }
    .mobile-bottom-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 40px;
    }
    .mobile-nav {
      display: flex;
      align-items: center;
    }
    .mobile-nav-link {
      display: flex;
      align-items: center;
      height: 40px;
      padding: 0 8px;
      text-decoration: none;
    }
    .mobile-nav-label {
      position: relative;
      display: flex;
      align-items: center;
      white-space: nowrap;
      font-family: "Noto Sans KR";
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 20px;
      color: #666666;
    }
    .mobile-nav-link.active .mobile-nav-label {
      color: #212121;
      font-weight: 700;
    }
    .mobile-nav-label-dot {
      position: absolute;
      top: -6px;
      left: 50%;
      width: 4px;
      height: 4px;
      border-radius: 999px;
      background-color: #212121;
      transform: translateX(-50%);
    }
    .mobile-nav-icons {
      display: flex;
      align-items: center;
      flex-shrink: 0;
    }
    .mobile-nav-icon-link {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      text-decoration: none;
    }
    .mobile-nav-icon-link + .mobile-nav-icon-link {
      margin-left: 4px;
    }
    .mobile-nav-icon-link img {
      width: 20px;
      height: 20px;
      object-fit: contain;
      display: block;
    }
  }
`;

const CommonHeaderTop = ({ currentPage = "air" }: { currentPage?: string }) => {
  const navigation = useNavigate();
  const { isScrolled } = useScrollDetection({ threshold: 10 });
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [headerTop, setHeaderTop] = useState<number>(0);
  const { isLoggedIn } = useUserQuery();
  const {
    setAlertSetting,
    isStartInterview,
    isLogoHidden,
    navRef,
    setList,
    setStoredList,
    setIsStartInterview,
    clearState,
    user,
    setUser,
    setHistory,
    headerRef,
    isMobile,
    openNav,
    setOpenNav,
    fetching,
    pageName,
    setIsLogin,
    referrerUrl,
    setReferrerUrl,
    isAppMode,
  } = useContext(MyContext);
  const { setLogoutConfirmOpen } = useAuth();

  // const isSticky = isScrolled;
  // 강제 고정 처리하엿음.
  const isSticky = false;

  const navItems = [
    {
      id: "news",
      label: "뉴스",
      href: "https://www.dongascience.com",
      isActive: currentPage === "news",
    },
    {
      id: "dl",
      label: "d라이브러리",
      href: "https://dl.dongascience.com",
      isActive: currentPage === "dl",
      // 어느 사이트를 보고 있든 d라이브러리에 워크지가 있다는 걸 알리는 딱지
      hint: "워크지 다운!",
    },
    {
      id: "air",
      label: "AIR",
      href: "https://air.dongascience.com",
      isActive: currentPage === "air",
    },
    {
      id: "dsstore",
      label: "DS스토어",
      href: "https://dsstore.dongascience.com/main",
      isActive: currentPage === "dsstore",
    },
    {
      id: "popcorn",
      label: "팝콘플래닛",
      href: "https://www.popcornplanet.co.kr",
      isExternal: true,
      mobileIcon:
        "https://img.dongascience.com/family-site/popcorn_symbol_mo_fit.svg",
    },
    {
      id: "jisatam",
      label: "지구사랑탐사대",
      href: "https://jisatam.dongascience.com",
      isExternal: true,
      mobileIcon:
        "https://img.dongascience.com/family-site/jisatam_symbol_mo_fit.svg",
    },
  ];
  // Separate mobile-only icon items from normal nav items
  const mobileIconItems = isMobile
    ? navItems.filter((it) => it.isExternal && it.mobileIcon)
    : [];
  const normalNavItems = navItems.filter(
    (it) => !(isMobile && it.isExternal && it.mobileIcon),
  );

  // 안내 딱지 노출 여부. 헤더가 축소(sticky)되면 띄울 공간이 없어 감춘다.
  // (이 저장소는 위에서 isSticky를 false로 고정해둬서 지금은 항상 노출된다.)
  // 딱지와 활성 표시 점이 메뉴명 위 같은 자리라 딱지가 점을 완전히 덮으므로,
  // 딱지가 보이는 동안에는 가려질 점을 아예 그리지 않는다 (#12091).
  // 두 조건이 어긋나면 점도 딱지도 없는 자리가 생기므로 한곳에서 판단한다.
  const hasVisibleHint = (item: { hint?: string }) =>
    Boolean(item.hint) && !isSticky;

  const logout = async () => {
    const response = await backIncstance("GET", URLS.LOGOUT);
    try {
      if (response.status === 200) {
        setUser((prev) => ({
          ...prev,
          id: null,
          tone: null,
          picture_extension: null,
          picture_data: null,
          name: null,
          paid: false,
          start_dt: null,
          end_dt: null,
          tutor_cnt: 0,
          socra_cnt: 0,
        }));
        sessionStorage.removeItem("userInfo");
        sessionStorage.removeItem("item");
        setHistory([]);
        setList([]);
        setStoredList([]);
        navigation("/");
      } else {
        setAlertSetting((prev) => ({
          ...prev,
          alertTxt: "다시 시도해주세요.",
          btnTxt: "닫기",
        }));
      }
    } catch {
      setAlertSetting((prev) => ({
        ...prev,
        alertTxt: "다시 시도해주세요.",
        btnTxt: "닫기",
      }));
    }
  };

  const isValidLogin = () => {
    // return loginInfo.id && loginInfo.accessToken && user?.id;
    return user && user.id ? true : false;
  };

  const handleClickLink = (target, item, requireLogin) => {
    if (isStartInterview) {
      setAlertSetting((prev) => ({
        ...prev,
        alertTxt: `페이지를 나가면 면접이 중단됩니다.\n 면접을 끝까지 완료하셔야 평가 리포트를 볼 수 있습니다.`,
        btnTxt: "중단하고 나가기",
        double: true,
        onConfirm: () => {
          if (typeof prev.onConfirm === "function") {
            prev.onConfirm();
          }
          moveToPage(target, item);
          setIsStartInterview(false);
        },
      }));
    } else if (requireLogin && !isValidLogin()) {
      window.location.assign(
        `${process.env.REACT_APP_LOGIN_URL}/?referer_url=${process.env.REACT_APP_REFERER_URL}/tutor`,
      );
    } else {
      moveToPage(target, item);
    }
  };

  const moveToPage = (target, item) => {
    if (
      (target === "마이페이지" || target === "나의 활동") &&
      !isValidLogin()
    ) {
      setIsLogin(true);

      // item(link)가 https://로 시작하는 경우 referrerUrl에 저장
      // 경로일 경우, hostname을 포함한 경로를 referrerUrl에 저장
      setReferrerUrl(
        typeof item === "string" &&
          (item.startsWith("http://") || item.startsWith("https://"))
          ? item
          : `${window.location.origin}${item}`,
      );
      return;
    }
    // 240627 로그인 정보 가져오기
    if (target === "정기구독인증") {
      alert("정기구독인증 페이지는 준비중입니다.");
      return;
    }
    if (target === "동아사이언스" && isMobile) {
      setOpenNav(!openNav);
    } else {
      setHistory([]);
      // 외부 URL인지 확인 (http:// 또는 https://로 시작하는지)
      if (
        typeof item === "string" &&
        (item.startsWith("http://") || item.startsWith("https://"))
      ) {
        // 외부 URL인 경우 window.location 또는 window.open 사용
        window.open(item, "_blank"); // 새 탭에서 열기
        // 또는 같은 탭에서 이동하려면: window.location.href = item;
      } else {
        // 내부 경로인 경우 React Router의 navigation 사용
        navigation(item, {
          state: {
            from_chat: null,
            is_writed: false,
            is_completed: false,
            his_cnt: 0,
          },
        });
      }
    }
  };

  const onClickLogout = () => {
    setLogoutConfirmOpen(true);
  };

  const getMarginBottom = () => {
    if (isMobile && isSticky) {
      return 0;
    }
    if (isMobile) {
      return 96;
    }
    if (isSticky) {
      return 0;
    }
    return 140;
  };

  // Update header top to sit directly under `.ui-top-banner.on` if present
  useEffect(() => {
    if (typeof window === "undefined") return;

    let ro: ResizeObserver | null = null;
    const updateTop = () => {
      const banner = document.querySelector(
        ".ui-top-banner.on",
      ) as HTMLElement | null;
      if (banner) {
        const rect = banner.getBoundingClientRect();
        // rect.bottom is relative to viewport; we want header top = rect.bottom
        setHeaderTop(Math.max(0, Math.round(rect.bottom)));
      } else {
        setHeaderTop(0);
      }
    };

    // mutation observer to catch class changes / banner added/removed
    const mo = new MutationObserver(() => updateTop());
    mo.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    // window listeners for resize/scroll
    window.addEventListener("resize", updateTop);
    window.addEventListener("scroll", updateTop);

    // if banner exists, observe size changes
    const bannerEl = document.querySelector(
      ".ui-top-banner.on",
    ) as HTMLElement | null;
    if (bannerEl && (window as any).ResizeObserver) {
      ro = new ResizeObserver(() => updateTop());
      ro.observe(bannerEl);
    }

    // initial
    updateTop();

    return () => {
      mo.disconnect();
      window.removeEventListener("resize", updateTop);
      window.removeEventListener("scroll", updateTop);
      if (ro && bannerEl) ro.unobserve(bannerEl);
    };
  }, []);

  return (
    <div style={{ marginBottom: getMarginBottom() }}>
      <StyledCommonHeaderTop
        isSticky={isSticky}
        isMobile={isMobile}
        top={headerTop}
        className={
          isMobile && isSticky
            ? "header-top-mo scroll common-header-top"
            : "common-header-top"
        }
      >
        {isMobile ? (
          <div className="mobile-wrapper">
            <div className="mobile-top-row">
              <LogoDsKo
                className="mobile-logo"
                onClick={() => (window.location.href = "/")}
              />
            </div>
            <div className="mobile-bottom-row">
              <nav className="mobile-nav">
                {normalNavItems.map((item) => (
                  <a
                    key={item.id}
                    href={item.href}
                    target={item.isExternal ? "_blank" : undefined}
                    rel={item.isExternal ? "noopener noreferrer" : undefined}
                    className={`mobile-nav-link ${
                      item.isActive ? "active" : ""
                    }`}
                  >
                    <span className="mobile-nav-label">
                      {item.isActive && !hasVisibleHint(item) && (
                        <span className="mobile-nav-label-dot" />
                      )}
                      {item.label}
                      {hasVisibleHint(item) && (
                        <Badge.Hint>{item.hint}</Badge.Hint>
                      )}
                    </span>
                  </a>
                ))}
              </nav>
              {mobileIconItems.length > 0 && (
                <div className="mobile-nav-icons">
                  {mobileIconItems.map((item) => (
                    <a
                      key={item.id}
                      href={item.href}
                      target={item.isExternal ? "_blank" : undefined}
                      rel={item.isExternal ? "noopener noreferrer" : undefined}
                      className="mobile-nav-icon-link"
                    >
                      <img src={item.mobileIcon} alt={item.label} />
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="wrapper">
            <div className="left">
              {isMobile && isSticky ? (
                <img
                  src="https://dl.dongascience.com/images/icons/dongascienceIconOn.svg"
                  alt="동아사이언스"
                  width={32}
                  height={32}
                  style={{ cursor: "pointer" }}
                  onClick={() => (window.location.href = "/")}
                />
              ) : (
                <LogoDsKo
                  className="logo"
                  onClick={() => (window.location.href = "/")}
                />
              )}
              {/* <LogoDsKo className="w-[118px] h-9 flex-shrink-0" /> */}
              <nav>
                {normalNavItems.map((item) => {
                  // 스크롤 시 외부 링크 숨기기 (기존 동작 유지)
                  if (isScrolled && item.isExternal) return null;

                  return (
                    <a
                      key={item.id}
                      href={item.href}
                      target={item.isExternal ? "_blank" : undefined}
                      rel={item.isExternal ? "noopener noreferrer" : undefined}
                    >
                      <span
                        className={`label-area ${
                          item.isActive ? "active" : ""
                        } ${item.isExternal ? "external-span" : ""}`}
                      >
                        {item?.isActive && !hasVisibleHint(item) && (
                          <div className="svg-area">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="4"
                              height="4"
                              viewBox="0 0 4 4"
                              fill="none"
                            >
                              <circle cx="2" cy="2" r="2" fill="#212121" />
                            </svg>
                          </div>
                        )}
                        {item.label}
                        {item.isExternal && (
                          <IcoExternal className="external-icon" />
                        )}
                        {hasVisibleHint(item) && (
                          <Badge.Hint>{item.hint}</Badge.Hint>
                        )}
                      </span>
                      {item.isActive && <div className="is-active" />}
                    </a>
                  );
                })}

                {/* 모바일에서 외부 사이트용 아이콘을 우측에 배치 */}
                {mobileIconItems.length > 0 && (
                  <div className="mobile-icons">
                    {mobileIconItems.map((item) => (
                      <a
                        key={item.id}
                        href={item.href}
                        target={item.isExternal ? "_blank" : undefined}
                        rel={
                          item.isExternal ? "noopener noreferrer" : undefined
                        }
                        className="mobile-icon-link"
                      >
                        <img src={item.mobileIcon} alt={item.label} />
                      </a>
                    ))}
                  </div>
                )}
              </nav>
            </div>
            <div className="right-wrapper">
              <div className="account-area">
                <div>
                  {isLoggedIn && (
                    <div className="member-wrap-flex">
                      <div className="member-wrap">
                        <strong className="b">{user?.name || ""}</strong>
                        <strong className="m">님</strong>
                        {/* {isVerifiedUser === true && <span>(정기구독회원)</span>} */}
                      </div>
                      <Button
                        className="unset logout-btn"
                        onClick={onClickLogout}
                      >
                        로그아웃
                      </Button>
                    </div>
                  )}
                  {!isLoggedIn && (
                    <a
                      href={`${ACCOUNT_DOMAIN}/?referer_url=${encodeURIComponent(
                        window
                          ? window.location.href
                          : process.env.REACT_APP_REFERER_URL!,
                      )}`}
                      className="login-link"
                    >
                      가입/로그인
                    </a>
                  )}
                </div>
              </div>
              <div className="icons">
                <a
                  href={"#"}
                  className="link"
                  onClick={(e) => {
                    e.preventDefault();
                    handleClickLink("이용가이드", "/guide/" + "main", false);
                  }}
                >
                  <img
                    src="https://img.dongascience.com/air/guide.svg"
                    alt="이용가이드"
                    width={24}
                    height={24}
                  />
                </a>
                <a
                  href={"#"}
                  className="link"
                  onClick={(e) => {
                    e.preventDefault();
                    handleClickLink(
                      "마이페이지",
                      process.env.REACT_APP_DONGA_MYPAGE ||
                        "https://my.dongascience.com",
                      true,
                    );
                  }}
                >
                  <img
                    src="https://img.dongascience.com/air/member-beta.svg"
                    alt="마이페이지"
                    width={24}
                    height={24}
                  />
                </a>
                <a
                  href={"#"}
                  className="link"
                  onClick={(e) => {
                    e.preventDefault();
                    handleClickLink("나의 활동", "/my-activity", true);
                  }}
                >
                  <img
                    src="https://img.dongascience.com/air/report.svg"
                    alt="나의 활동"
                    width={24}
                    height={24}
                  />
                </a>
              </div>
            </div>
          </div>
        )}
      </StyledCommonHeaderTop>
    </div>
  );
};

export default CommonHeaderTop;
